import { useMemo } from 'react'
import sortBy from 'lodash/sortBy'

import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'

import { ExtendedVariant } from '../ContentTable'

const useModifiedVariants = (
  variants: ExtendedVariant[] | undefined,
  refreshSuccessVariantIds: string[],
  moreLikeThisSuccessVariantIds: string[]
) => {
  const { content } = useContent()
  const { data: selectedElement } = useSelectedComponentElement()

  return useMemo(() => {
    return sortBy(
      variants?.map((variant) => {
        const recommended = variant?.sortOrder === 1
        let isRefreshing = false
        let isLoadingMoreLikeThis = false

        if (content && selectedElement) {
          const refreshTopic = getWsTopic({
            action: 'refreshVariant',
            data: {
              contentId: content._id,
              elementId: selectedElement.element_id,
              variantId: String(variant.variant_id),
            },
          })

          isRefreshing =
            content.ws_messages?.some(
              (message) => message.wsTopic === refreshTopic
            ) || false

          const moreLikeThisWsTopic = getWsTopic({
            action: 'moreLikeThisVariants',
            data: {
              contentId: content._id,
              elementId: selectedElement.element_id,
              variantId: String(variant.variant_id),
            },
          })

          isLoadingMoreLikeThis =
            content.ws_messages?.some(
              (message) => message.wsTopic === moreLikeThisWsTopic
            ) || false
        }

        const isDisabled = isRefreshing || isLoadingMoreLikeThis

        const subRows = variant.subRows?.map((subRow) => {
          let isSubRowRefreshing = false

          if (content && selectedElement) {
            const refreshTopic = getWsTopic({
              action: 'refreshVariant',
              data: {
                contentId: content._id,
                elementId: selectedElement.element_id,
                variantId: String(subRow.variant_id),
              },
            })
            isSubRowRefreshing =
              content.ws_messages?.some(
                (message) => message.wsTopic === refreshTopic
              ) || false
          }

          return {
            ...subRow,
            recommended: false,
            isDisabled: isSubRowRefreshing || isDisabled,
            isRefreshing: isSubRowRefreshing,
            isRefreshed: refreshSuccessVariantIds.includes(
              String(subRow.variant_id)
            ),
            isRefreshSuccessful: refreshSuccessVariantIds.includes(
              String(subRow.variant_id)
            ),
            checked: selectedElement?.selected_variants?.includes(
              subRow.variant_id
            ),
          }
        })

        return {
          ...variant,
          isDisabled,
          recommended,
          isRefreshing,
          isLoadingMoreLikeThis,
          isRefreshed:
            refreshSuccessVariantIds.includes(String(variant.variant_id)) ||
            moreLikeThisSuccessVariantIds.includes(String(variant.variant_id)),
          isRefreshSuccessful: refreshSuccessVariantIds.includes(
            String(variant.variant_id)
          ),
          isMoreLikeThisSuccessful: moreLikeThisSuccessVariantIds.includes(
            String(variant.variant_id)
          ),
          checked: selectedElement?.selected_variants?.includes(
            variant.variant_id
          ),
          subRows,
        }
      }),
      [(item) => (item.recommended === true ? 0 : 1)]
    )
  }, [
    variants,
    content,
    selectedElement,
    refreshSuccessVariantIds,
    moreLikeThisSuccessVariantIds,
  ])
}

export default useModifiedVariants
